// lib/slides/validationPoints.ts
//
// SINGLE SOURCE OF TRUTH for the URW / Westfield inline-unit 10-point design
// validation grid. Both the Fater Reviewer agent prompt
// (lib/prompt/slidesKnowledgeBase.ts) and the exported review card
// (app/api/slides/export/route.ts) read this list, so the axis a verdict is
// keyed under and the row it prints on can never drift apart.
//
// Positions and labels follow the FINAL URW Europe validation card. The `pN`
// ids remain attached to their existing concepts even where FINAL moved a row:
// persisted reviews are keyed by id, so renumbering ids would silently attach
// old comments to the wrong subject. Thresholds are distilled from the "RDM
// Guidelines – Inline Units 2026" rulebook, cited in each axis' `criteria`.

import type { SlideReviewVerdict } from '#types/types';

export const ELEMENT_VERDICT_COLORS: Record<SlideReviewVerdict, string> = {
  pass: '#4DAB9A', warn: '#FFA344', fail: '#FF7369', missing: '#FFDC49', na: '#979A9B',
};

export interface SlidesValidationPoint {
  /** Stable id used in review state + annotations (guidelinePointId). */
  id: string;
  /** 1..10 - the row number on the official validation card. */
  position: number;
  /** Official French label shown on the card. */
  fr: string;
  /** English label for the agent's internal reasoning. */
  en: string;
  /** One-line source rule + the deterministic threshold(s), for the prompt. */
  criteria: string;
  /** True for the "structural" points whose failure alone forces a C verdict. */
  structural: boolean;
}

/** The 10 axes keyed by their persisted ids. Exported below in FINAL card order. */
const SLIDES_VALIDATION_POINTS_BY_ID: readonly SlidesValidationPoint[] = [
  {
    id: 'p1', position: 1, fr: 'Transparence visuelle', en: 'Visual transparency', structural: true,
    criteria: 'Within the 3 m shopfront zone, ≥75% of the facade must read through to the back of the shop (≤25% opacity). Opaque surfaces only as high-quality architectural accents. Standard glazing, if needed: full-height, frameless, tempered + laminated; rails concealed. (p.4)',
  },
  {
    id: 'p2', position: 2, fr: 'Façade ouverte', en: 'Open shopfront', structural: true,
    criteria: 'No permanent doors or obstructing elements; wide, fluid transition with the mall during opening hours. Security tags concealed (floor/hung); if on gates, thin/transparent, behind the closing line, no advertising. Full-height opening, or clear height ≥3000 mm. (p.5)',
  },
  {
    id: 'p3', position: 3, fr: 'Expression toute hauteur', en: 'Full-height expression', structural: true,
    criteria: 'Full-height 3D architectural activation, floor to ceiling; ≥1 full-height element in the zone. Flank walls activated with non-scratchable architectural-grade finishes. Entrance ceiling flush with the mall ceiling. NO horizontal bulkhead/fascia band in the zone (case-by-case derogation only, then decorated). (p.6)',
  },
  {
    id: 'p4', position: 7, fr: 'Visual merchandising créatif', en: 'Creative visual merchandising', structural: false,
    criteria: 'Brand-specific, scenographic VM; sightlines preserved. Displays set back ≥10 cm behind glass (or ≥1 m in open space); standard peripheral furniture set back ≥80 cm from the lease line. NO cash desks and NO cosmetic services in the zone. (p.10)',
  },
  {
    id: 'p5', position: 6, fr: 'Supports de communication', en: 'Brand graphics', structural: false,
    criteria: 'Framed graphics/lightboxes set back or recessed. Total graphic surface <15% of the facade; window vinyl ≤10% of glazing, applied inside. NO digital screens in the zone; background screens ≤300 cd/m², no third-party or flashing content. Temporary commercial vinyl ≤8 weeks, inside, with CM approval. (p.9)',
  },
  {
    id: 'p6', position: 8, fr: 'Système de fermeture', en: 'Closure system', structural: true,
    criteria: '≥50% transparent, closed system; colour free. Transparent panels (glass/polycarbonate) OK; perforated metal grilles/shutters FORBIDDEN. Fully concealed by day unless part of the concept. Horizontal roller discouraged; if imposed: set back, fully hidden in the plenum, key switch 30 cm above FFL. Full-height, or clear height ≥3000 mm. (p.11)',
  },
  {
    id: 'p7', position: 4, fr: 'Activation de la retombée', en: 'Void space', structural: false,
    criteria: 'The void (mall-height) activated with 3D elements/installations across all its surfaces. Where limited, the bulkhead is activated in 3D by a second skin; overhang above the lease line ≤20 cm, subject to clear height ≥220 cm and neighbouring sightlines. (p.7)',
  },
  {
    id: 'p8', position: 5, fr: 'Enseigne de façade', en: 'Shopfront signage', structural: false,
    criteria: 'One main sign per facade (approved trading name only), on the lease line, not behind glazing; corner units max two, one per elevation. Individual backlit 3D letters - lightboxes FORBIDDEN. Secondary sign only if tone-on-tone and ≥50% smaller. No place names, product descriptions, or straplines. (p.8)',
  },
  {
    id: 'p9', position: 9, fr: 'Matériaux de haute qualité', en: 'High quality materials', structural: true,
    criteria: 'Architectural-grade materials on facade, exposed surfaces, flank walls and floor. ELIMINATORY: any material that scratches to reveal a different colour underneath is refused. Forbidden: plasterboard, veneers, laminates, acrylic, Foamex, plastic, MDF, vinyl, painted metals / large powder-coated panels, synthetic/carpet/vinyl floors. (p.12)',
  },
  {
    id: 'p10', position: 10, fr: 'Soumission du dossier design', en: 'Design submission', structural: true,
    criteria: 'Reviewed only if complete: location/layout/demolition plans, reflected ceiling + lighting, digital material board, facade concept (3D colour + 2D elevation), facade detail (3 m zone sections), internal sections/lightboxes, signage details, VM/furniture direction. One bound PDF with lease line, gridlines, dimensions and notes. Missing piece → status C until complete. (p.3, p.13)',
  },
] as const;

/** FINAL card order, while ids keep existing review data attached to its concept. */
export const SLIDES_VALIDATION_POINTS: readonly SlidesValidationPoint[] =
  [...SLIDES_VALIDATION_POINTS_BY_ID].sort((a, b) => a.position - b.position);

/** Fixed captions from FINAL Retail - 10P + Authorities, including its formulas. */
export type SlidesStampLanguage = 'en' | 'fr';
const STAMP_STRINGS = {
  "en": {
    "title": "CONCEPT DESIGN VALIDATION & 3m SHOPFRONT ZONE",
    "meta": {
      "rdm": "RDM",
      "unit": "Unit",
      "date": "Date",
      "centre": "Centre",
      "review": "Issue"
    },
    "commentsBar": "Review Status & Remarks",
    "authoritiesRow": "Authorities submission",
    "statusRow": "Design validation status",
    "legalFooter": "The tenant remains responsible for taking into account the Landlord's comments, for fulfilling all requirements of our legal team, for submitting the documents, and for complying with legislation, regulations, specifications, and codes of practice for design, manufacturing, installation, and commissioning.",
    "authorities": {
      "pass": "Can be submitted to authorities.",
      "fail": "Cannot be submitted to authorities. Action required."
    },
    "status": {
      "A": "Design approved.",
      "B": "Design conditionally approved, subject to the provided comments being incorporated into the final design. Outstanding items must be resubmitted for final approval.",
      "C": "Design not approved. Please revise and resubmit in line with the comments provided."
    },
    "corner": {
      "tenant": "TENANT",
      "notAValidation": "FOR COMMENTS ONLY. THIS IS NOT A VALIDATION."
    }
  },
  "fr": {
    "title": "VALIDATION DU CONCEPT DESIGN & DE LA ZONE D'ENTREE DE 3m",
    "meta": {
      "rdm": "RDM",
      "unit": "Cellule",
      "date": "Date",
      "centre": "Centre",
      "review": "Retour"
    },
    "commentsBar": "Commentaires",
    "authoritiesRow": "Dépôt du DA",
    "statusRow": "Statut de la validation",
    "legalFooter": "Le preneur reste responsable de la prise en compte des commentaires du bailleur, de l'exécution de toutes les exigences de notre équipe juridique, de la soumission des documents et du respect de la législation, des réglementations, des spécifications et des codes d'usage pour la conception, la fabrication, l'installation et la mise en service.",
    "authorities": {
      "pass": "Le projet peut être soumis aux autorités.",
      "fail": "Le projet ne peut pas être soumis aux autorités. Des actions correctives sont requises."
    },
    "status": {
      "A": "Le projet est approuvé.",
      "B": "Le projet est approuvé sous réserve de l’intégration des commentaires figurant sur cette page dans la conception finale. Les éléments manquants ou en suspens devront être soumis à nouveau pour approbation finale.",
      "C": "Le projet n’est pas approuvé. Veuillez le réviser et le soumettre à nouveau conformément aux commentaires fournis."
    },
    "corner": {
      "tenant": "PRENEUR",
      "notAValidation": "POUR COMMENTAIRES. PAS UNE VALIDATION."
    }
  }
} as const;

/** FINAL workbook colors shared by the editor and PDF. */
export const STAMP_COLORS = {
  blue: '#4055FF', text: '#1B2733', number: '#1F6FEB',
  band: '#EEF0FE', grid: '#C9D9F0', footer: '#5A6B7B',
} as const;

/** Default when a deck does not say. English keeps existing decks unchanged. */
export const DEFAULT_STAMP_LANGUAGE: SlidesStampLanguage = 'en';

export const stampStrings = (lang?: string) =>
  STAMP_STRINGS[lang === 'fr' ? 'fr' : DEFAULT_STAMP_LANGUAGE];

export const stampPointLabel = (point: SlidesValidationPoint, lang?: string): string =>
  lang === 'fr' ? point.fr : point.en;

/**
 * Whether the dossier can go to the authorities, INDEPENDENT of the A/B/C
 * design verdict.
 *
 * These were coupled: the authorities line was derived from the status, so a
 * "B, approved with reservations" always printed "cannot be submitted". Thibaut
 * asked for them separated because they are separate judgements, and a scheme
 * can be filable while still carrying design reservations. When the reviewer has
 * not said, the old derivation is the fallback so nothing changes by itself.
 */
export const resolveAuthorityStatus = (
  authorityStatus: 'pass' | 'fail' | undefined,
  designStatus: SlidesValidationStatus
): 'pass' | 'fail' => authorityStatus ?? (designStatus === 'A' ? 'pass' : 'fail');

export type SlidesValidationStatus = 'A' | 'B' | 'C';

/** How each verdict paints on the validation card cell, for BOTH renderers: the
 *  HTML card (components/slides/SlidesValidationCard.tsx) and the exported PDF
 *  (app/api/slides/export/route.ts, via hexToRgb). One table, because the card
 *  the reviewer clicks and the stamp the tenant receives must not drift.
 *  NA is grey rather than amber on purpose: it used to share the amber "action
 *  requested" cell, so a criterion that did not apply read as an outstanding
 *  item on the tenant's list. */
export type VerdictMark = 'check' | 'cross' | 'empty' | 'dash';

export const VERDICT_MARK: Record<SlideReviewVerdict, { mark: VerdictMark; color: string }> = {
  pass: { mark: 'check', color: '#A9D08E' },
  warn: { mark: 'empty', color: '#FFD966' },
  fail: { mark: 'cross', color: '#ED7D31' },
  missing: { mark: 'empty', color: '#FFD966' },
  na: { mark: 'dash', color: '#E4E8EC' },
};

/** The A/B/C letter cell reuses the verdict palette: A reads as pass, B as
 *  action requested, C as fail. */
export const STATUS_CELL_COLOR: Record<SlidesValidationStatus, string> = {
  A: VERDICT_MARK.pass.color,
  B: VERDICT_MARK.warn.color,
  C: VERDICT_MARK.fail.color,
};

/** Bullet list of the axes for embedding in the agent prompt (id · FR · criteria). */
export function renderValidationPointsForPrompt(): string {
  // English-first for the agent prompt: URW's default language is English and the
  // guidelines deck + validation card are English. The `fr` label is kept in the
  // data model (card/export can still use it) but is not rendered into the prompt.
  return SLIDES_VALIDATION_POINTS.map(
    (p) => `**Row ${p.position} (key ${p.id}) - ${p.en}**${p.structural ? ' [structural]' : ''}. ${p.criteria}`,
  ).join('\n\n');
}
